import React, { useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import BuildChildren from 'utils/BuildChildren';
import { priceFormatter } from 'utils/Numbers';
import { buildProductStructuredData } from 'utils/StructuredData';
import className from 'classnames';
import AffirmContainer from 'containers/AffirmContainer/AffirmContainer';
import Link from 'components/Link';
import classes from './CardProductE.module.scss';

const CardProductE = (props) => {
  const {
    imageURI,
    imageAltText = '',
    imageHeight,
    imageWidth,
    displayName,
    description,
    shortDescription,
    price,
    originalPrice,
    button,
    isPartOfList = false,
    isAffirmEligible,
    productDetailUrl,
    url,
  } = props;

  const [descriptionCollapsed, toggleCollapsed] = React.useState(true);
  const [showToggleCollapseButton, setShowToggleCollapseButton] = React.useState(true);
  const descriptionField = useRef(null);
  const hasProductDetailPage = productDetailUrl && url;
  const itemDescription = shortDescription || description;
  const DisplayNameTag = hasProductDetailPage ? Link : 'div';

  useLayoutEffect(() => {
    const updateToggleCollapse = () => {
      if (descriptionField.current !== null) {
        const updatedShowToggleCollapse = +classes.heightlimittocollapse < descriptionField.current.scrollHeight;
        if (showToggleCollapseButton !== updatedShowToggleCollapse) {
          setShowToggleCollapseButton(updatedShowToggleCollapse);
          toggleCollapsed(updatedShowToggleCollapse);
        }
      }
    };
    const debouncedHandleResize = debounce(updateToggleCollapse, 500);

    window.addEventListener('resize', debouncedHandleResize);
    updateToggleCollapse();

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  });

  return (
    <div className={classes.container}>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: buildProductStructuredData(props) }}
      />
      <div className={classes.wrapper}>
        {imageURI &&
          <div className={classes.imageContainer}>
            <img
              className={classes.image}
              src={imageURI}
              alt={imageAltText}
              width={imageWidth}
              height={imageHeight}
              loading='lazy'
              aria-hidden
            />
          </div>}
        <div className={className(classes.productInfoContainer, { [classes.onList]: isPartOfList })}>
          {displayName &&
            <DisplayNameTag
              href={productDetailUrl}
              className={classes.displayName}
              dangerouslySetInnerHTML={{ __html: displayName }}
            />}
          {price >= 0 &&
            <div className={classes.price}>
              {priceFormatter(price)}
            </div>}
          {originalPrice > price &&
            <div className={classes.originalPrice}>
              {priceFormatter(originalPrice)}
            </div>}
          {price >= 0 &&
            <div className={classes.affirmMessage}>
              <AffirmContainer price={price} isAffirmEligible={isAffirmEligible} className={classes.affirmMessage} />
            </div>}
          {itemDescription &&
            <div
              className={className(
                classes.description, { [classes.collapsed]: descriptionCollapsed || hasProductDetailPage },
              )}
              dangerouslySetInnerHTML={{ __html: itemDescription }}
              ref={descriptionField}
            />}
          <div className={classes.moreDetails}>
            {!hasProductDetailPage && itemDescription && showToggleCollapseButton &&
            <button
              aria-label='More Details'
              className={classes.moreDetailsButton}
              onClick={() => toggleCollapsed(!descriptionCollapsed)}
              type='button'
            >
              <i
                className={
                  className(
                    descriptionCollapsed ? 'far fa-plus-circle' : 'far fa-minus-circle',
                    classes.moreDetailsIcon,
                  )
                }
              />
              { descriptionCollapsed ? 'More Details' : 'Less Details' }
            </button>}
            {hasProductDetailPage &&
            <Link
              href={productDetailUrl}
              className={classes.moreDetailsButton}
              dangerouslySetInnerHTML={{ __html: 'View Details' }}
            />}
          </div>
          {button &&
            <div className={classes.footerWrapper}>
              <div className={classes.itemContainer}>
                <div className={classes.button}>
                  {BuildChildren(button)}
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>
  );
};

const ButtonT = PropTypes.shape({
  subType: PropTypes.string.isRequired,
  title: PropTypes.string,
  href: PropTypes.string,
  target: PropTypes.string,
  leftIcon: PropTypes.string,
  rightIcon: PropTypes.string,
  onClick: PropTypes.func,
});

CardProductE.propTypes = {
  /**
   * Image URL
  */
  imageURI: PropTypes.string,
  /**
   * Image alternate text that displays on hover over of image
  */
  imageAltText: PropTypes.string,
  /**
   * Pixel height of image
  */
  imageHeight: PropTypes.number,
  /**
   * Pixel width of image
  */
  imageWidth: PropTypes.number,
  /**
   * Text displays at the top of the component
  */
  displayName: PropTypes.string,
  /**
   * Text displays directly below description
  */
  description: PropTypes.string,
  /**
  * Text displays directly below short description
 */
  shortDescription: PropTypes.string,
  /**
   * Product price, used for structured data
  */
  price: PropTypes.number,
  /**
   * Shown only if value is greater than `price`
  */
  originalPrice: PropTypes.number,
  /**
   * Button Object
   * Refer to documentation on `Button` component
  */
  button: ButtonT,
  /**
   * Product is part of a list<br>
   * Options: `true` `false`
  */
  isPartOfList: PropTypes.bool,
  /**
   * Can you use affirm on this product
  */
  isAffirmEligible: PropTypes.bool,
  /**
   * URL to product detail page
  */
  productDetailUrl: PropTypes.string,
  /**
   * URL to product
  */
  url: PropTypes.string,
};

export default CardProductE;
